import React, { useState, useEffect } from "react";
import type { MajorArcanaCard } from "../data/majorArcana";
import { getJournalEntry, saveJournalEntry, getLocalDateKey } from "../lib/dailyDraw";

type TabId = "overview" | "prompt" | "affirmation" | "journal";

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: "overview", label: "Meaning", icon: "📖" },
  { id: "prompt", label: "Reflect", icon: "💭" },
  { id: "affirmation", label: "Affirm", icon: "✨" },
  { id: "journal", label: "Journal", icon: "📝" },
];

export function CardTabs({ card }: { card: MajorArcanaCard }) {
  const [active, setActive] = useState<TabId>("overview");
  const [journalText, setJournalText] = useState("");
  const todayKey = getLocalDateKey();

  useEffect(() => {
    setJournalText(getJournalEntry(card.id, todayKey));
  }, [card.id, todayKey]);

  const handleJournalChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setJournalText(val);
    saveJournalEntry(card.id, todayKey, val);
  };

  return (
    <div className="w-full">
      <div className="grid grid-cols-4 gap-2 mb-6" role="tablist" aria-label="Card details">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={active === t.id}
            className={`
              py-3 px-2 text-sm font-bold rounded-xl transition-all duration-200
              focus:outline-none focus:ring-2 ring-joy-400
              ${
                active === t.id
                  ? "bg-gradient-to-br from-joy-400 to-joy-600 text-white shadow-lg scale-105"
                  : "bg-joy-50 text-slate-600 hover:bg-joy-100"
              }
            `}
            onClick={() => setActive(t.id)}
          >
            <div className="text-lg mb-1">{t.icon}</div>
            <div className="text-xs">{t.label}</div>
          </button>
        ))}
      </div>

      <div className="min-h-[220px] bg-gradient-to-br from-joy-50/50 to-white/50 rounded-xl p-6 border border-joy-100" role="tabpanel">
        {active === "overview" && (
          <div className="animate-fade-in">
            <div className="text-2xl leading-relaxed text-slate-800 font-serif">{card.shortMeaning}</div>
          </div>
        )}

        {active === "prompt" && (
          <div className="animate-fade-in">
            <div className="text-2xl leading-relaxed text-slate-700 font-serif italic">"{card.prompt}"</div>
          </div>
        )}

        {active === "affirmation" && (
          <div className="animate-fade-in">
            <div className="text-2xl leading-relaxed text-joy-800 font-serif font-semibold">{card.affirmation}</div>
          </div>
        )}

        {active === "journal" && (
          <div className="animate-fade-in">
            <textarea
              className="w-full h-44 p-4 bg-white border-2 border-joy-200 rounded-xl focus:ring-2 focus:ring-joy-500 focus:border-joy-500 resize-none text-base text-slate-700 placeholder-slate-400 shadow-inner transition-all"
              placeholder="How does this card speak to you today? Write your thoughts..."
              value={journalText}
              onChange={handleJournalChange}
            />
            <div className="mt-3 flex items-center justify-between text-xs">
              <span className="text-slate-500">Notes for {todayKey}</span>
              <span className="text-joy-600 font-medium">✓ Auto-saved</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
